import { useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'

import styles from './HipaaBaa.module.scss'

import { signEnterpriseHipaaBaa } from '@/api/enterprise'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import Input from '@/components/Input'
import { useToast } from '@/components/Toast/ToastContext'
import { AppDispatch } from '@/store'
import { getEnterpriseThunk, selectCurrentEnterprise } from '@/store/features/user/userSlice'
import { compactAmPm } from '@/utils/compactAmPm'

const HIPAA_BAA_URL = 'https://wispr-flow-cdn.s3.us-west-2.amazonaws.com/policies/hipaa-baa/latest.pdf'

function formatSignedDate(isoDate: string): string {
  const date = new Date(isoDate)
  return compactAmPm(date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
  }))
}

interface HipaaBaaProps {
  isOpen: boolean
  onClose: () => void
}

export default function HipaaBaa(props: HipaaBaaProps) {
  const { isOpen, onClose } = props
  const dispatch = useDispatch<AppDispatch>()
  const [legalName, setLegalName] = useState('')
  const [isSigning, setIsSigning] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const enterprise = useSelector(selectCurrentEnterprise)
  const { updateToast } = useToast()

  const handleSign = useCallback(async () => {
    try {
      setIsSigning(true)
      // eslint-disable-next-line
      const { data } = await signEnterpriseHipaaBaa({ legal_name: legalName })
      dispatch(getEnterpriseThunk({ needSubscription: false }))
      updateToast(data.message, 'success')
      onClose()
    } catch (error) {
      console.error('Failed to sign HIPAA BAA', error)
      updateToast(error instanceof Error ? error.message : String(error), 'error')
    } finally {
      setIsSigning(false)
    }
  }, [legalName, dispatch, updateToast, onClose])

  const handleDownload = useCallback(async () => {
    if (!enterprise) return
    try {
      setIsDownloading(true)
      const res = await fetch(HIPAA_BAA_URL)
      if (!res.ok) throw new Error(`Failed to fetch PDF: ${res.status}`)
      const pdfBytes = await res.arrayBuffer()
      const pdfDoc = await PDFDocument.load(pdfBytes)

      const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold)
      const page = pdfDoc.addPage([612, 792]) // US Letter

      const textColor = rgb(0.2, 0.2, 0.2)
      const labelColor = rgb(0.4, 0.4, 0.4)
      let y = 680

      page.drawText('Signature Record', { x: 72, y, font: boldFont, size: 20, color: textColor })
      y -= 40

      page.drawText('This HIPAA Business Associate Agreement was electronically signed.', {
        x: 72,
        y,
        font,
        size: 11,
        color: textColor,
      })
      y -= 18
      page.drawText('This page is generated for reference only and is not a legal certificate.', {
        x: 72,
        y,
        font,
        size: 9,
        color: labelColor,
      })
      y -= 50

      const fields: [string, string][] = [
        ['Signed by', enterprise.hipaaBaaSignedBy || 'N/A'],
        ['Email', enterprise.hipaaBaaSignedByEmail || 'N/A'],
        ['Date', enterprise.hipaaBaaSignedAt ? formatSignedDate(enterprise.hipaaBaaSignedAt) : 'N/A'],
        ['Organization', enterprise.name],
      ]

      for (const [label, value] of fields) {
        page.drawText(label, { x: 72, y, font, size: 10, color: labelColor })
        page.drawText(value, { x: 72, y: y - 16, font: boldFont, size: 12, color: textColor })
        y -= 45
      }

      const modifiedPdfBytes = await pdfDoc.save()
      const blob = new Blob([new Uint8Array(modifiedPdfBytes)], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `HIPAA-BAA-${enterprise.name.replace(/\s+/g, '-')}-signed.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download HIPAA BAA', error)
      updateToast('Failed to download agreement', 'error')
    } finally {
      setIsDownloading(false)
    }
  }, [enterprise, updateToast])

  return (
    <DialogWrapper
      isOpen={isOpen}
      title="HIPAA Business Associate Agreement"
      close={onClose}
      minWidth={'min(90vw, 1000px)'}
    >
      <div className={styles.container}>
        <iframe className={styles.iframe} src={HIPAA_BAA_URL} />
        {enterprise?.hipaaBaaSigned ? (
          <>
            <span className={styles.signedInfo}>
              Signed by {enterprise.hipaaBaaSignedBy || 'an admin'}
              {enterprise.hipaaBaaSignedByEmail && ` (${enterprise.hipaaBaaSignedByEmail})`}
              {enterprise.hipaaBaaSignedAt && ` on ${formatSignedDate(enterprise.hipaaBaaSignedAt)}`}.
            </span>
            <span className={styles.signedInfo}>
              Data sharing is disabled for all users in your organization while this agreement is active.
            </span>
            <Button title="Download signed agreement" onClick={handleDownload} type="dark" isLoading={isDownloading} />
          </>
        ) : (
          <>
            <span className={styles.confirmation}>
              *By clicking &quot;I agree&quot;, you agree to the HIPAA Business Associate Agreement. Signing ensures
              that your organization&apos;s transcription data will not be used to train or improve models. This remains
              enforced as long as the agreement is valid.
            </span>
            <Input
              value={legalName}
              type="text"
              onInput={setLegalName}
              placeholder="Enter your legal name"
              className={styles.input}
            />
            <Button title="I agree" onClick={handleSign} type="dark" isDisabled={!legalName} isLoading={isSigning} />
          </>
        )}
      </div>
    </DialogWrapper>
  )
}
